const FINISHED_STATUSES = ['Completado', 'Finalizado'];

/** Tarifa horaria de mano de obra en factura (IVA 22% incluido). Independiente del presupuesto del servicio. */
export const INVOICE_LABOR_HOURLY_IVA_INCLUDED = 4041;

export function isPendingInvoiceService(vehicleService) {
  if (!vehicleService || vehicleService.invoiceId) return false;
  const statusName =
    vehicleService.status?.name ||
    vehicleService.status?.description ||
    '';
  return FINISHED_STATUSES.includes(String(statusName).trim());
}

export function getServiceLaborHours(vehicleService) {
  const totalMinutes = Number(vehicleService?.totalTime) || 0;
  if (totalMinutes <= 0) return 0;
  return Math.round((totalMinutes / 60) * 100) / 100;
}

/** Total MO a facturar: horas de reparación × tarifa fija con IVA. */
export function getLaborCost(vehicleService) {
  const hours = getServiceLaborHours(vehicleService);
  if (hours <= 0) return 0;
  return Math.round(hours * INVOICE_LABOR_HOURLY_IVA_INCLUDED * 100) / 100;
}

export function getServiceDisplayName(vehicleService) {
  return (
    vehicleService?.serviceName ||
    vehicleService?.service?.name ||
    vehicleService?.description ||
    'Servicio'
  );
}

export function mapUsedProductLines(vehicleService, getUnitValue) {
  const resolveUnit = getUnitValue || ((unit) => unit || 'un');

  return (vehicleService?.usedProducts || [])
    .filter((up) => Number(up.quantity) > 0)
    .map((up, index) => {
      const name =
        up.productName ||
        up.product?.name ||
        up.product?.code ||
        'Producto';
      const quantity = Number(up.quantity) || 0;
      const price = Number(up.price) || 0;

      return {
        id: `product-${vehicleService.id}-${up.id ?? index}`,
        concepto: name,
        unidad: resolveUnit(up.product?.unit?.name) || 'un',
        cantidad: quantity,
        cantidad_input: String(quantity),
        precio: price,
        precio_input: String(price),
        indicador_facturacion: 3,
        ivaIncluded: false,
        iva_porcentaje: 22,
        cod_item: up.product?.code || undefined,
        currencyCode: up.product?.currency?.code || up.currency?.code || 'UYU',
        type: 'used-product',
      };
    });
}

export function buildLaborLine(vehicleService, getUnitValue) {
  const hours = getServiceLaborHours(vehicleService);
  if (hours <= 0) return null;

  const serviceName = getServiceDisplayName(vehicleService);
  const resolveUnit = getUnitValue || ((unit) => unit || 'un');

  return {
    id: `labor-${vehicleService.id}`,
    concepto: `Mano de obra - ${serviceName}`,
    unidad: resolveUnit('h'),
    cantidad: hours,
    cantidad_input: String(hours),
    precio: INVOICE_LABOR_HOURLY_IVA_INCLUDED,
    precio_input: String(INVOICE_LABOR_HOURLY_IVA_INCLUDED),
    indicador_facturacion: 3,
    ivaIncluded: true,
    iva_porcentaje: 22,
    type: 'labor',
  };
}

export function buildInvoiceLinesFromVehicleService(
  vehicleService,
  { getUnitValue, calculateItemTotals } = {},
) {
  const lines = [];
  const laborLine = buildLaborLine(vehicleService, getUnitValue);
  if (laborLine) {
    if (calculateItemTotals) calculateItemTotals(laborLine);
    lines.push(laborLine);
  }

  mapUsedProductLines(vehicleService, getUnitValue).forEach((line) => {
    if (calculateItemTotals) calculateItemTotals(line);
    lines.push(line);
  });

  return lines;
}

export function summarizeVehicleServiceForInvoice(vehicleService) {
  const productLines = mapUsedProductLines(vehicleService);
  const laborCost = getLaborCost(vehicleService);
  const productsTotal = productLines.reduce(
    (sum, line) => sum + line.cantidad * line.precio,
    0,
  );

  return {
    serviceName: getServiceDisplayName(vehicleService),
    laborCost,
    products: productLines.map((line) => ({
      name: line.concepto,
      quantity: line.cantidad,
      price: line.precio,
      total: line.cantidad * line.precio,
      unit: line.unidad,
    })),
    productsTotal,
    totalCost: laborCost + productsTotal,
    hasLabor: laborCost > 0,
    hasProducts: productLines.length > 0,
  };
}
